import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import Layout from 'components/common/layout/Layout';
import SingleBtn from 'components/common/elements/SingleBtn';
import TableForm from 'components/common/lists/TableForm';
import Paging from 'components/common/nav/Paging';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { DEFAULT_PAGE_SIZE } from 'constants/page';
import { ROUTE_PATH } from 'constants/routePath';
import { TGetNoticeListParams } from 'types/apis/notice';
import { TTableHeader } from 'types/components/table';
import { getNoticeList } from 'apis/notice';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from 'store/auth';

const tableColumns: TTableHeader[] = [
  { name: 'no', label: '번호', width: '120px' },
  {
    name: 'title',
    label: '제목',
    width: '620px',
    align: 'left',
  },
  { name: 'writerName', label: '작성자', width: '120px' },
  {
    name: 'createdAt',
    label: '등록일',
    width: '160px',
    onClick: () => {
      return;
    },
  },
];

const NoticeList = () => {
  const userInfo = useAtomValue(userInfoAtom);
  const isStudent = userInfo?.userRole === 'STUDENT';

  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const [page, setPage] = useState(Number(query.get('page')) || 1);
  const [params, setParams] = useState<TGetNoticeListParams>({
    page,
    size: DEFAULT_PAGE_SIZE,
    sortBy: 'createdAt',
    isAsc: query.get('isAsc') ? query.get('isAsc') === 'asc' : false,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    query.set('size', params.size.toString());
    query.set('page', params.page.toString());
  }, []);

  useEffect(() => {
    query.set('isAsc', params.isAsc ? 'asc' : 'desc');

    navigate({
      search: query.toString(),
    });
  }, [params.isAsc]);

  useEffect(() => {
    setParams({
      ...params,
      page,
    });
  }, [page]);

  useEffect(() => {
    if (query.get('isAsc')) return;
    if (page !== 1) {
      setPage(1);
    }
    setParams({
      ...params,
      page: 1,
      isAsc: query.get('isAsc') ? query.get('isAsc') === 'asc' : false,
    });
    query.set('size', DEFAULT_PAGE_SIZE.toString());
    query.set('page', '1');
    query.set('isAsc', query.get('isAsc') || 'desc');

    navigate({
      search: query.toString(),
    });
  }, [location.search]);

  const { data: getNoticeListData, isFetching } = useQuery(
    ['getNoticeList', params],
    () => getNoticeList(params)
  );

  const noticeListData = getNoticeListData?.data.noticeDslResponse || [];
  const noticeListPaging = getNoticeListData?.data.paging;

  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  const onClickSort = () => {
    if (page !== 1) {
      setPage(1);
    }
    setParams({
      ...params,
      isAsc: !params.isAsc,
    });
  };

  const onClickNoticeDetail = (id: string) =>
    navigate(`${ROUTE_PATH.common.NOTICE}/${id}`, {
      state: {
        from: {
          pathname: location.pathname,
          search: location.search,
        },
      },
    });

  return (
    <Layout>
      <div className="flex items_center justify_between mt40 mb24">
        <h1 className="main_title">공지사항</h1>
        {!isStudent && (
          <SingleBtn
            btnText="공지사항 등록"
            isBtnActive={true}
            btnStyle={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '33px',
              padding: '8px 16px',
              fontSize: '14px',
            }}
            icon={
              <PlusIcon
                width={14}
                height={14}
                fill={STYLES_VARIABLES.WHITE}
                style={{ marginRight: '2px' }}
              />
            }
            onClick={() =>
              navigate(ROUTE_PATH.common.NOTICE_REGISTER, {
                state: {
                  from: {
                    pathname: location.pathname,
                    search: location.search,
                  },
                },
              })
            }
          />
        )}
      </div>

      <TableForm
        data={noticeListData}
        columns={tableColumns}
        paging={noticeListPaging}
        sortBy="createdAt"
        sortDirection={params.isAsc ? 'asc' : 'desc'}
        onClickSort={onClickSort}
        onClickRowById={onClickNoticeDetail}
        idField="noticeId"
        emptyMessage="등록된 공지사항이 없습니다."
        isLoading={isLoading}
      />

      {noticeListPaging && noticeListPaging.totalElements > 0 && (
        <div className="mt32">
          <Paging
            activePage={page}
            totalItemsCount={noticeListPaging.totalElements}
            handlePageChange={setPage}
          />
        </div>
      )}
    </Layout>
  );
};

export default NoticeList;
